import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, VirtualMachineIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useVirtualMachine } from '../../../hooks/useVirtualMachine';
import { useVmPowerStatus } from '../../../hooks/useVmPowerStatus';
import renderPowerStateLabel from './powerStateLabel';
import VmPowerActions from './VmPowerActions';
import VmOverviewTab from './VmOverviewTab';
import VmSnapshotsTab from './VmSnapshotsTab';
import VmHardwareTab from './VmHardwareTab';
import VmNetworkTab from './VmNetworkTab';
import VmConsoleTab from './VmConsoleTab';
import VmEmailTab from './VmEmailTab';

function VirtualMachineDetail() {
  const { vmId } = useParams<{ vmId: string }>();
  const auth = useAuth();
  const { vm, loading, error, refetch } = useVirtualMachine(vmId!);
  const { powerState, isLoading: powerStateLoading, refetchPowerStatus } = useVmPowerStatus(vm?.id ?? null);
  const [activeTabKey, setActiveTabKey] = useState<string | number>(0);

  // The replica Alert links between two VM detail pages on the same route; the component
  // instance survives that param-only navigation, so the tab selection must be reset by hand.
  useEffect(() => {
    setActiveTabKey(0);
  }, [vmId]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleTabClick = (
    _event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent | MouseEvent,
    tabIndex: string | number,
  ) => {
    setActiveTabKey(tabIndex);
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/virtual-machines">Virtual Machines</BreadcrumbItem>
            <BreadcrumbItem isActive>...</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <VirtualMachineIcon />
            </Icon>
            Virtual Machine
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <CenteredSpinner />
        </PageSection>
      </>
    );
  }

  if (error || !vm) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/virtual-machines">Virtual Machines</BreadcrumbItem>
            <BreadcrumbItem isActive>Not found</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <VirtualMachineIcon />
            </Icon>
            Virtual Machine
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <EmptyState
            titleText="Unable to load virtual machine"
            icon={ExclamationCircleIcon}
            variant="lg"
            headingLevel="h2"
            status="danger"
          >
            <EmptyStateBody>{error ? error.message : 'Virtual machine not found.'}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => refetch()}>
                  Retry
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </PageSection>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/virtual-machines">Virtual Machines</BreadcrumbItem>
          <BreadcrumbItem isActive>{vm.name}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <VirtualMachineIcon />
              </Icon>
              {vm.name}
            </Content>
            <Content component="p">
              {renderPowerStateLabel(powerState, powerStateLoading)} Virtual machine details and management
            </Content>
          </FlexItem>
          <FlexItem>
            <VmPowerActions vm={vm} powerState={powerState} onPowerActionSuccess={refetchPowerStatus} />
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        {vm.replica_of && (
          <Alert
            variant="info"
            isInline
            component="div"
            className="pf-v6-u-mb-md"
            title={
              <>
                This virtual machine is a DRP replica of{' '}
                <Link to={`/datacenter/virtual-machines/${vm.replica_of.id}`}>
                  <strong>{vm.replica_of.name}</strong>
                </Link>
              </>
            }
          />
        )}
        {/* mountOnEnter: several tabs fetch their own data (Overview → prices, Snapshots, Network,
            Console → VNC websocket), so a panel only mounts — and fetches — when first opened. */}
        <Tabs activeKey={activeTabKey} onSelect={handleTabClick} mountOnEnter>
          <Tab eventKey={0} title={<TabTitleText>Overview</TabTitleText>}>
            <VmOverviewTab vm={vm} powerState={powerState} powerStateLoading={powerStateLoading} onVmUpdate={refetch} />
          </Tab>
          <Tab eventKey={1} title={<TabTitleText>Snapshots</TabTitleText>}>
            <VmSnapshotsTab vmId={vm.id!} vmName={vm.name} />
          </Tab>
          <Tab eventKey={2} title={<TabTitleText>Hardware</TabTitleText>}>
            <VmHardwareTab />
          </Tab>
          <Tab eventKey={3} title={<TabTitleText>Network</TabTitleText>}>
            <VmNetworkTab vmId={vm.id!} />
          </Tab>
          <Tab eventKey={4} title={<TabTitleText>Console</TabTitleText>}>
            <VmConsoleTab vmId={vm.id!} />
          </Tab>
          {vm.is_imap && (
            <Tab eventKey={5} title={<TabTitleText>Email</TabTitleText>}>
              <VmEmailTab />
            </Tab>
          )}
        </Tabs>
      </PageSection>
    </>
  );
}

export default VirtualMachineDetail;
